import type { ReactNode } from 'react';
import { ChevronDown, Folder, Phone, Pin, Video } from 'lucide-react';
import type { ChatSummary } from '../api/client.ts';
import { useOpenFolders, toggleFolder } from '../lib/botFolders.ts';
import { buildChatList, folderLead, folderTime, folderUnread } from '../lib/chatList.ts';
import { formatBotCount, formatListTimestamp } from '../lib/format.ts';
import { useHold } from '../lib/hold.ts';
import { parseMessage, smileyUrl } from '../lib/smileys.ts';
import { ChatAvatar } from './ChatAvatar.tsx';
import { Loading } from './Loading.tsx';
import { TypingDots } from './TypingDots.tsx';
import './ChatList.css';

/**
 * The snippet is the raw message body, so a smiley arrives here as its code —
 * shown small rather than as `:wink:`, which is the whole point of the codes
 * being an implementation detail.
 */
function SnippetText({ text }: { text: string }) {
  return (
    <>
      {parseMessage(text).map((part, index) =>
        part.type === 'text' ? (
          part.value
        ) : (
          <img
            key={index}
            className="chat-row-smiley"
            src={smileyUrl(part.slug)}
            alt={part.label}
          />
        ),
      )}
    </>
  );
}

/** The badge, drawn the same at the right edge of a chat row and of a folder. */
function Unread({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <div className="chat-row-unread" aria-label={`Непрочитанных: ${count}`}>
      {count > 99 ? '99+' : count}
    </div>
  );
}

function ChatListRow({
  chat,
  active,
  typing,
  nested,
  labelled,
  onSelect,
  onHold,
}: {
  chat: ChatSummary;
  active: boolean;
  typing: boolean;
  /** Inside an open folder: indented, on the folder's own ground, smaller tile. */
  nested?: boolean;
  /** Draw the «БОТ» tag — true only where the divider is not doing that job. */
  labelled?: boolean;
  onSelect: (chat: ChatSummary) => void;
  onHold: (chat: ChatSummary) => void;
}) {
  // A bot's row answers a tap and nothing else: the only thing the menu offers
  // is a pin, and a bot cannot be pinned — the divider it lives under is not a
  // sort key anything may beat. A menu with one greyed-out row would be worse
  // than no menu, so the gesture is simply not armed there.
  const held = useHold(() => onHold(chat));

  return (
    <button
      type="button"
      className={`chat-row${active ? ' chat-row-active' : ''}${nested ? ' chat-row-nested' : ''}`}
      onClick={() => onSelect(chat)}
      {...(chat.bot ? {} : held)}
    >
      <ChatAvatar
        chat={chat}
        className={`chat-row-avatar${chat.bot ? ' chat-row-avatar-bot' : ''}`}
        glyph={20}
      />
      <div className="chat-row-body">
        <div className="chat-row-top">
          <div className="chat-row-name">{chat.title}</div>
          {labelled && chat.bot && <span className="chat-row-bot">БОТ</span>}
          {/* Beside the time rather than out at the right edge with the badge:
              this is a fact about the row, the way «БОТ» is, and the badge's
              column is a count that lines up down the list. */}
          {chat.pinnedAt !== null && (
            <Pin className="chat-row-pin" size={12} aria-label="Закреплено" />
          )}
          {chat.lastMessage && (
            <div className="chat-row-time">
              {formatListTimestamp(chat.lastMessage.createdAt)}
            </div>
          )}
        </div>
        {/* Takes the snippet's place, as it does in Telegram — the row has one
            line for it, and «печатает…» is the more useful of the two. */}
        {typing ? (
          <div className="chat-row-snippet chat-row-typing">
            печатает
            <TypingDots />
          </div>
        ) : (
          <div className="chat-row-snippet">
            {chat.lastMessage ? (
              <>
                {/* «Игорь: созвон в 15:00» — in a group the snippet is only
                    half the story without who said it. */}
                {chat.kind === 'group' && chat.lastMessage.senderName !== null && (
                  <span className="chat-row-sender">{chat.lastMessage.senderName}: </span>
                )}
                {/* A call's snippet is already a whole sentence — the glyph in
                    front of it is what makes it scannable against a column of
                    text. The server writes the wording; see callPreview. */}
                {chat.lastMessage.kind === 'call' &&
                  (chat.lastMessage.callVideo ? (
                    <Video className="chat-row-call-icon" size={13} />
                  ) : (
                    <Phone className="chat-row-call-icon" size={13} />
                  ))}
                <SnippetText text={chat.lastMessage.preview} />
              </>
            ) : (
              'Нет сообщений'
            )}
          </div>
        )}
      </div>
      {/* Its own column at the right edge rather than a second thing in
          .chat-row-top: the time is already there, and a badge that shifted
          with the length of the title would stagger down the list. */}
      <Unread count={chat.unreadCount} />
    </button>
  );
}

/**
 * A folder of bots: a row that opens and closes rather than one that leads
 * anywhere.
 *
 * There is no screen behind it and there should not be — two to four rows do
 * not need one, and a trip out of the list to read them would cost more than it
 * saved. What it shows is what the rows under it would have shown together:
 * the newest message, with the name of the bot that sent it, and one badge for
 * everything unread inside.
 */
function FolderRow({
  name,
  chats,
  open,
  onToggle,
}: {
  name: string;
  chats: ChatSummary[];
  open: boolean;
  onToggle: () => void;
}) {
  const lead = folderLead(chats);
  const at = folderTime(chats);

  return (
    <button
      type="button"
      className="chat-row chat-folder"
      aria-expanded={open}
      onClick={onToggle}
    >
      <div className="chat-row-avatar chat-folder-tile">
        <Folder size={20} />
      </div>
      <div className="chat-row-body">
        <div className="chat-row-top">
          <div className="chat-row-name chat-folder-name">{name}</div>
          <span className="chat-folder-count">{formatBotCount(chats.length)}</span>
          {at > 0 && <div className="chat-row-time">{formatListTimestamp(at)}</div>}
        </div>
        <div className="chat-row-snippet">
          {lead?.lastMessage ? (
            <>
              <span className="chat-row-sender">{lead.title}: </span>
              <SnippetText text={lead.lastMessage.preview} />
            </>
          ) : (
            'Нет сообщений'
          )}
        </div>
      </div>
      <Unread count={folderUnread(chats)} />
      <ChevronDown
        className={`chat-folder-chevron${open ? ' chat-folder-chevron-open' : ''}`}
        size={16}
      />
    </button>
  );
}

export function ChatList({
  chats,
  activeChatId,
  loading,
  filtered,
  typingChatIds,
  onSelect,
  onHold,
  after,
}: {
  chats: ChatSummary[];
  activeChatId: string | null;
  loading: boolean;
  /** True when a search is narrowing the list — changes the empty message. */
  filtered: boolean;
  /** Conversations where the other person is typing right now. */
  typingChatIds: string[];
  onSelect: (chat: ChatSummary) => void;
  /** A long press, or a right click: what opens the menu over a row. */
  onHold: (chat: ChatSummary) => void;
  /**
   * Drawn under the rows, inside this scroller: the messages the same search
   * found. Here rather than beside the list because two scrollers in one
   * sidebar is two places to be in, and the answer to one word is one column.
   */
  after?: ReactNode;
}) {
  const open = useOpenFolders();

  // A name that matches nothing can still be a word that appears in messages,
  // so «Ничего не найдено» is only true when neither half found anything.
  if (chats.length === 0 && !after) {
    // The list that has not arrived is not the list that is empty, and both of
    // them used to be the same blank column: on a slow phone «Пока нет чатов»
    // would have been a lie, so it was held back and nothing took its place.
    if (loading) return <Loading />;
    return (
      <div className="chat-list chat-list-empty">
        {filtered ? 'Ничего не найдено' : 'Пока нет чатов'}
      </div>
    );
  }

  const row = (chat: ChatSummary, options?: { nested?: boolean; labelled?: boolean }) => (
    <ChatListRow
      key={chat.id}
      chat={chat}
      active={chat.id === activeChatId}
      typing={typingChatIds.includes(chat.id)}
      nested={options?.nested}
      labelled={options?.labelled}
      onSelect={onSelect}
      onHold={onHold}
    />
  );

  // A search answers with the chats that matched and nothing else: the divider
  // and the folders are how a whole list is read, and neither means anything
  // once a word has already narrowed it to four rows.
  if (filtered) {
    return (
      <div className="chat-list">
        {/* Tagged here, unlike below the divider: a narrowed list mixes people
            and bots with nothing between them to say which is which. */}
        {chats.map((chat) => row(chat, { labelled: true }))}
        {after}
      </div>
    );
  }

  return (
    <div className="chat-list">
      {buildChatList(chats).map((entry) => {
        if (entry.kind === 'chat') return row(entry.chat);
        if (entry.kind === 'divider') {
          // A label, not a control: it does not collapse the section and never
          // takes focus, so tabbing through the list still walks the chats.
          return (
            <div className="chat-divider" key="divider">
              <span className="chat-divider-label">БОТЫ</span>
            </div>
          );
        }
        const shown = open.has(entry.name);
        return (
          <div className="chat-folder-block" key={`folder:${entry.name}`}>
            <FolderRow
              name={entry.name}
              chats={entry.chats}
              open={shown}
              onToggle={() => toggleFolder(entry.name)}
            />
            {shown && entry.chats.map((chat) => row(chat, { nested: true }))}
          </div>
        );
      })}
      {after}
    </div>
  );
}
